/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React from 'react';
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown
} from 'reactstrap';
import { SurveyListItem } from '../../types/survey';
import SimpleTooltip from '../simple-tooltip';
import { isAggregatedSurvey } from '../../utils/survey';

interface SurveyListDropdownProps {
  selectedItem?: SurveyListItem;
  handleDropdownSelect?: (item: SurveyListItem) => void;
  showAutomaticReport?: boolean;
  surveyList: SurveyListItem[];
  disabled?: boolean;
}

type SurveyListDropdownType = {
  [key in ['inCompletedSurveys', 'completedSurveys', 'combinedReports'] as string]: SurveyListItem[];
};

export const SurveyListDropdown = (props: SurveyListDropdownProps) => {
  const { selectedItem, showAutomaticReport = true, handleDropdownSelect = () => { }, disabled = false } = props;

  const list = props.surveyList;
  if (list.length === 0 || !selectedItem) {
    return null;
  }

  const { inCompletedSurveys, completedSurveys, combinedReports } = list.reduce(
    (acc, survey) => {
      if (isAggregatedSurvey(survey.type)) {
        acc.combinedReports.push(survey);
        return acc;
      }
      if (!survey.completedDate && survey._id) {
        acc.inCompletedSurveys.push(survey);
      }
      if (!!survey.completedDate) {
        acc.completedSurveys.push(survey);
      }
      return acc;
    },
    { inCompletedSurveys: [], completedSurveys: [], combinedReports: [] } as SurveyListDropdownType
  );

  const currentTotal = list.find(data => !data._id);

  return <UncontrolledDropdown>
    <DropdownToggle className={'text-truncate'} style={{ maxWidth: '350px ' }} color='transparent' caret disabled={disabled}>
      <SimpleTooltip text={`${disabled ? 'You need to complete at least one survey.' : ''}`}>
        <span className='px-2'>{selectedItem.name}</span>
      </SimpleTooltip>
    </DropdownToggle>
    <DropdownMenu end>
      {(currentTotal && showAutomaticReport)
        ?
        <>
          <DropdownItem header>
            Automatic Report
          </DropdownItem>

          <DropdownItem onClick={() => handleDropdownSelect(currentTotal)}>
            <i className={'fas fa-shapes mr-2'} />
            Current Total
          </DropdownItem>
          {list.length > 1 ? <DropdownItem divider /> : null}
        </>
        :
        null
      }
      {combinedReports.length ? <>
        <DropdownItem header>
          Combined Surveys
        </DropdownItem>
        {combinedReports.map((item: SurveyListItem, i: number) => {
          if (item.completedDate) {
            return <DropdownItem key={`rs-${item._id}-${i}`} active={selectedItem._id === item._id} onClick={() => handleDropdownSelect(item)}>
              <i className={'fas fa-object-exclude icon mr-2'} />
              {item.name}
            </DropdownItem>
          }
          return <DropdownItem key={`rs-${item._id}-${i}`} active={selectedItem._id === item._id} onClick={() => handleDropdownSelect(item)}>
            <i className={'fas fa-object-exclude icon mr-2'} />
            {item.name}
          </DropdownItem>
        })}
        <DropdownItem divider />
      </>
        : null
      }
      {completedSurveys.length ? <>
        <DropdownItem header>
          Completed Surveys
        </DropdownItem>
        {completedSurveys.map((item: SurveyListItem, i: number) => (
          <DropdownItem key={`rs-${item._id}-${i}`} active={selectedItem._id === item._id} onClick={() => handleDropdownSelect(item)}>
            <i className={'fa fa-check-circle mr-2'} />
            {item.name}
          </DropdownItem>
        ))}
        <DropdownItem divider />
      </>
        : null
      }
      {inCompletedSurveys.length ? <>
        <DropdownItem header>
          Private Surveys
        </DropdownItem>
        {inCompletedSurveys.map((item: SurveyListItem, i: number) => (
          <DropdownItem key={`rs-${item._id}-${i}`} active={selectedItem._id === item._id} onClick={() => handleDropdownSelect(item)}>
            <i className={'fa fa-circle mr-2'} />
            {item.name}
          </DropdownItem>
        ))}
        <DropdownItem divider />
      </>
        : null
      }
    </DropdownMenu>
  </UncontrolledDropdown>
}
